import { HomeGreeting } from '../../ui/HomeGreeting';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  familyAppointmentDone,
  familyCarePlan,
  familyCircleAccess,
  familyConferenceRequest,
  familyCoverage,
  familyDateAdd,
  familyDashboard,
  familyDateReminders,
  familyDates,
  familyDoctorSummary,
  familyEventAck,
  familyInbox,
  familyLinkRequest,
  familyMedOutcome,
  familyMessageSend,
  familyMomentAdd,
  familyMoments,
  familyMyResidents,
  familyNoticeSeen,
  familyNotices,
  familyPanelSeen,
  familyPortal,
  familyRequest,
  familyTaskAct,
  familyTaskAdd,
  familyThread,
  familyTodo,
  familyTodoNudgeDone,
  familyTodoStepDone,
  familyNoticeCalled,
  familyVisits,
  familyWeeklyEmail,
  familyWeek,
  weekBars,
  type ApiError,
  type OnboardingMe,
  type Role,
  familyLifeStoryPdfPath,
  familyInviteResend,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { useQuery } from '../../useQuery';
import { useReadOnly, useReadOnlyReason } from '../../ui/ReadOnly';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { sx } from '../../ui/designStyles';
import { buildModel, type FpSources } from './familyModel';
import { familyVals, fsuVals, type FpActs, type FpUi } from './familyVals';
import { FpHeadView } from './FpHeadView';
import { FpUnlinkedView } from './FpUnlinkedView';
import { FpSetupView } from './FpSetupView';
import { FpHomeView } from './FpHomeView';
import { FpTodayCard } from './FpTodayCard';
import { FpV9Top } from './FpV9Top';
import { FpActivityRow, FpSummaryRow } from './FpV9Row';
import { tierLabel } from '../residents/common';
import { FpPageHeadView } from './FpPageHeadView';
import { FpBodyView } from './FpBodyView';
import './family.css';

// A port of the Console V3 design's Family Portal (T17077-17899, logic L39476-40240). The
// markup is the design's own; every figure comes from the family routes (packages/shared
// family-routes.ts). Role changes what the surface is: a family member sees their
// resident; the Facility Admin and Aithora see coverage, and the family's view of the
// resident whose family is waiting longest.

type Persona = 'Family' | 'Operator' | 'Facility Admin';
export function personaOf(role: Role | null): Persona | null {
  if (role === 'FAMILY_MEMBER') return 'Family';
  if (role === 'ADMIN') return 'Facility Admin';
  // Aithora (Super Admin, Operator) sees the read-only Coverage view only (Andrew, 2026-09-30).
  if (role === 'SUPER_ADMIN' || role === 'OPERATOR') return 'Operator';
  return null;
}

const TAB_LABEL: Record<FpUi['tab'], string> = { home: 'Home', access: 'People & access', today: 'Today', visits: 'Visits', appt: 'Doctor visits', plan: 'Care plan', messages: 'Messages', health: 'Health', moments: 'Moments', dates: 'Dates to remember', tasks: 'To do' };

export function FamilyPortal() {
  const { role } = useConsoleRole();
  const persona = personaOf(role);
  const [ui, setUiState] = useState<FpUi>({
    tab: persona === 'Operator' ? 'access' : 'home', threadKey: 'care', draft: '', sent: [], code: '', codeMsg: '', accessMsg: '', visitAsk: '', planRaised: {}, apptDone: {}, quickUsed: {},
    panelAck: {}, eventSeen: {}, medOut: {}, summaryOpen: false, askedMeeting: false, tdNew: '', called: {},
  });
  usePageTab(persona ? (ui.tab === 'access' && persona === 'Operator' ? 'Coverage' : TAB_LABEL[ui.tab]) : null);
  if (!persona) return null;
  return <FamilyPortalPage persona={persona} ui={ui} setUiState={setUiState} />;
}

function FamilyPortalPage({ persona, ui, setUiState }: { persona: Persona; ui: FpUi; setUiState: (f: (s: FpUi) => FpUi) => void }) {
  const { session, client } = useSession();
  const { realRole } = useConsoleRole();
  const readOnly = useReadOnly();
  const roReason = useReadOnlyReason();
  const navigate = useNavigate();
  const fam = persona === 'Family';
  // Aithora reads coverage only: never a family's thread, notices or a resident's view.
  const aithora = persona === 'Operator';
  const viewerId = session?.user.id ?? null;
  const setUi = (p: Partial<FpUi>) => setUiState((s) => ({ ...s, ...p }));
  const [err, setErr] = useState<ApiError | null>(null);
  const [fsu, setFsu] = useState({ open: false, hidden: false, testSent: false, hello: null as 'msg' | 'photo' | null, date: null as string | null });
  const useQ = <T,>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[], enabled = true) => useQuery<T>(load, [client, session?.token, ...deps], { enabled });

  // Which resident: a family member's first linked resident; staff open on the family
  // thread that has waited longest (the inbox, scoped by the server).
  const mine = useQ((signal) => familyMyResidents(client, { signal }), [], fam);
  const inbox = useQ((signal) => familyInbox(client, { signal }), [], !fam && !aithora);
  const coverage = useQ((signal) => familyCoverage(client, { signal }), [], !fam);
  const staffPick = inbox.data?.threads.slice().sort((a, b) => Number(b.overdue) - Number(a.overdue) || (b.waitingHours ?? 0) - (a.waitingHours ?? 0))[0] ?? null;
  const rid = aithora ? null : fam ? mine.data?.residents[0]?.id ?? null : staffPick?.residentId ?? null;
  const org = fam ? mine.data?.residents[0]?.organizationId ?? null : staffPick?.organizationId ?? null;
  const on = !!rid;
  const d = [rid];
  const notices = useQ((signal) => familyNotices(client, { params: { residentId: rid! }, signal }), d, on);
  const thread = useQ((signal) => familyThread(client, { params: { residentId: rid! }, signal }), d, on);
  const moments = useQ((signal) => familyMoments(client, { params: { residentId: rid! }, signal }), d, on);
  const dates = useQ((signal) => familyDates(client, { params: { residentId: rid! }, signal }), d, on);
  const circle = useQ((signal) => familyCircleAccess(client, { params: { residentId: rid! }, signal }), d, on);
  const visits = useQ((signal) => familyVisits(client, { params: { residentId: rid! }, signal }), d, on);
  const plan = useQ((signal) => familyCarePlan(client, { params: { residentId: rid! }, signal }), d, on);
  const doctor = useQ((signal) => familyDoctorSummary(client, { params: { residentId: rid! }, signal }), d, on);
  const portal = useQ((signal) => familyPortal(client, { params: { residentId: rid! }, signal }), d, on);
  const dashboard = useQ((signal) => familyDashboard(client, { query: { organizationId: org! }, signal }), [org], fam && !!org);
  const onboarding = useQ<OnboardingMe>((signal) => api.onboardingMe(client, { signal }), [], fam);
  // V4 To do: the notice checklists, Staying close and the shared list (family only).
  const todo = useQ((signal) => familyTodo(client, { query: { resident: rid! }, signal }), d, on && fam);
  const [photoSent, setPhotoSent] = useState(false);
  // Why a photo did not reach her Atlas (the moment was saved, the file was not).
  const [photoNote, setPhotoNote] = useState<string | null>(null);

  const src: FpSources = {
    persona, viewerId, resident: fam ? mine.data?.residents[0] ?? null : staffPick ? { id: staffPick.residentId, name: staffPick.residentName ?? '–', organizationId: staffPick.organizationId, room: staffPick.room, facilityName: staffPick.facilityName } : null,
    notices: notices.data, thread: thread.data, moments: moments.data, dates: dates.data, circle: circle.data, visits: visits.data, plan: plan.data,
    doctor: doctor.data, portal: portal.data, coverage: coverage.data, inbox: inbox.data, dashboard: dashboard.data, photoSent, photoNote, todo: todo.data,
  };
  const M = useMemo(() => buildModel(src, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persona, viewerId, rid, notices.data, thread.data, moments.data, dates.data, circle.data, visits.data, plan.data, doctor.data, portal.data, coverage.data, inbox.data, dashboard.data, photoSent, photoNote, mine.data, todo.data]);
  // A client-generated id per write, so a retried tap (or a replay after a dropped
  // connection) lands once on the server.
  const clientId = () => (globalThis.crypto && 'randomUUID' in globalThis.crypto ? globalThis.crypto.randomUUID() : 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10));

  const run = <T,>(p: Promise<T>, then?: (r: T) => void) => { setErr(null); p.then((r) => then && then(r)).catch((e: ApiError) => setErr(e)); };
  const guard = (fn: () => void) => () => { if (!readOnly) fn(); };
  const send = (body: string, to: FpUi['threadKey']) => {
    if (readOnly || !rid) return;
    if (to !== 'care') {
      // The front desk and family threads are PROPOSED: the request goes as a family request.
      run(familyRequest(client, { params: { residentId: rid }, body: { kind: 'thread:' + to, text: body } }), () => setUiState((s) => ({ ...s, sent: s.sent.concat([{ body, to }]), draft: '' })));
      return;
    }
    run(familyMessageSend(client, { params: { residentId: rid }, body: { body, organizationId: org ?? undefined } }), () => {
      setUiState((s) => ({ ...s, sent: s.sent.concat([{ body, to }]), draft: '' }));
      thread.reload();
    });
  };
  const acts: FpActs = {
    setUi,
    requestLink: (code) => run(familyLinkRequest(client, { body: { code } }), (r) => setUi({ code: '', codeMsg: r.message })),
    send,
    ask: (body) => { setUi({ threadKey: 'care' }); send(body, 'care'); },
    noticeSeen: (n) => guard(() => run(familyNoticeSeen(client, { params: { residentId: rid!, noticeId: n.id }, body: {} }), () => { setUi({ eventSeen: { ...ui.eventSeen, ['n:' + n.id]: true } }); notices.reload(); }))(),
    eventAck: (id) => guard(() => run(familyEventAck(client, { params: { residentId: rid!, eventId: id }, body: {} }), () => setUi({ eventSeen: { ...ui.eventSeen, ['e:' + id]: true } })))(),
    panelSeen: (label) => guard(() => run(familyPanelSeen(client, { params: { residentId: rid!, label }, body: {} }), () => setUi({ panelAck: { ...ui.panelAck, [label]: true } })))(),
    // The moment, then its file, the way the phone sends it (FamilyMomentsScreen): it says
    // the photo is on her Atlas only once the server has stored the picture (QA-06).
    photo: (file) => guard(() => {
      setPhotoSent(false);
      setPhotoNote(null);
      run((async () => {
        // V7: a voice note goes the same way as a photo (the route takes mediaType 'voice').
        const r = await familyMomentAdd(client, { params: { residentId: rid! }, body: { mediaType: file.type.startsWith('audio/') ? 'voice' : 'photo', caption: 'Sent to ' + (M.resident?.first ?? 'her'), contentType: file.type, byteSize: file.size } });
        if (!r.upload.live || !r.upload.path) return { stored: false, reason: r.upload.reason };
        const res = await client.raw(r.upload.path, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        let j: { error?: unknown; real?: boolean; reason?: unknown } | null = null;
        try { j = await res.json(); } catch { /* not JSON */ }
        if (!res.ok) throw new Error(typeof j?.error === 'string' ? j.error : 'The photo did not send. Try again.');
        if (j?.real === false) return { stored: false, reason: String(j.reason ?? 'The photo could not be stored yet.') };
        return { stored: true, reason: '' };
      })(), (out) => {
        if (out.stored) setPhotoSent(true);
        else setPhotoNote('Saved, but the photo itself cannot be stored yet. ' + out.reason);
        moments.reload();
      });
    })(),
    taskAct: (id, action) => guard(() => run(familyTaskAct(client, { params: { residentId: rid!, taskId: id }, body: { action } }), () => { portal.reload(); todo.reload(); }))(),
    taskAdd: (text) => guard(() => run(familyTaskAdd(client, { params: { residentId: rid! }, body: { text, clientId: clientId() } }), () => { setUi({ tdNew: '' }); portal.reload(); todo.reload(); }))(),
    stepDone: (stepId) => guard(() => run(familyTodoStepDone(client, { params: { stepId }, body: { clientId: clientId() } }), () => todo.reload()))(),
    nudgeDone: (key) => guard(() => run(familyTodoNudgeDone(client, { params: { key }, body: { resident: rid!, clientId: clientId() } }), () => todo.reload()))(),
    noticeCalled: (n) => guard(() => run(familyNoticeCalled(client, { params: { residentId: rid!, noticeId: n.id }, body: {} }), () => setUiState((s) => ({ ...s, called: { ...s.called, [n.id]: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) } }))))(),
    remind: (id, patch) => guard(() => run(familyDateReminders(client, { params: { residentId: rid!, dateId: id }, body: patch }), () => dates.reload()))(),
    weekly: (v) => guard(() => run(familyWeeklyEmail(client, { body: { on: v } }), () => portal.reload()))(),
    meeting: () => guard(() => run(familyConferenceRequest(client, { params: { residentId: rid! }, body: { organizationId: org ?? undefined } }), () => { setUi({ askedMeeting: true }); plan.reload(); }))(),
    familyRequest: (kind, text, onOk) => guard(() => run(familyRequest(client, { params: { residentId: rid! }, body: { kind, text } }), () => { onOk?.(); portal.reload(); }))(),
    apptDone: (i) => guard(() => run(familyAppointmentDone(client, { params: { residentId: rid!, index: String(i) }, body: {} }), () => setUi({ apptDone: { ...ui.apptDone, [i]: true } })))(),
    // The choice shows once the server has it (clearing a choice records nothing).
    medOutcome: (i, name, outcome) => guard(() => {
      if (!outcome) { setUi({ medOut: { ...ui.medOut, [i]: null } }); return; }
      run(familyMedOutcome(client, { params: { residentId: rid! }, body: { medication: name, outcome } }), () => setUi({ medOut: { ...ui.medOut, [i]: outcome } }));
    })(),
    openResidents: () => navigate('/residents'),
    lifeStory: () => {
      if (!rid) return;
      // Reading her Life Story is allowed in a read-only view too: it writes nothing.
      run((async () => {
        const res = await client.raw(familyLifeStoryPdfPath(rid));
        if (!res.ok) {
          let msg = `The Life Story could not be opened (${res.status}).`;
          try { const j = await res.json(); if (j?.error) msg = String(j.error); } catch { /* not JSON */ }
          throw new Error(msg);
        }
        const url = URL.createObjectURL(await res.blob());
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })() as Promise<unknown>);
    },
    // Says only what the server did: sent, or why not.
    resendInvite: (inviteId, who) => guard(() => run(familyInviteResend(client, { params: { residentId: rid!, inviteId }, body: {} }), (r) => {
      setUi({ accessMsg: r.sent ? 'A new invite link was emailed to ' + who + '. Nothing is shared until she verifies.' : 'The invite for ' + who + ' was renewed but not sent: ' + (r.reason ?? 'the email did not go.') });
      circle.reload();
    }))(),
  };
  const V = familyVals(M, ui, acts, !fam || !!rid || !mine.data);

  // Family setup (design fsu*): from GET /onboarding/me, written through the real routes.
  const ob = onboarding.data && onboarding.data.kind === 'family' && 'steps' in onboarding.data ? (onboarding.data as unknown as { steps: Array<{ key: string; done: boolean; channel?: string | null; testAckSeconds?: number | null; readToResidentAt?: string | null; dateId?: string | null }>; completedAt: string | null; show: boolean }) : null;
  const step = (k: string) => ob?.steps.find((s) => s.key === k);
  const S = fsuVals({
    first: M.resident?.first ?? 'her',
    s: {
      channel: (step('channel')?.channel as 'sms' | 'push' | 'email' | null) ?? null,
      testAckSeconds: step('channel')?.testAckSeconds ?? null,
      testSent: fsu.testSent || step('channel')?.testAckSeconds != null,
      hello: fsu.hello ?? (step('first_message')?.done ? 'msg' : null),
      readAt: step('first_message')?.readToResidentAt ?? null,
      date: fsu.date ?? (step('date')?.done ? 'saved' : null),
      complete: !!ob?.completedAt,
    },
    open: fsu.open,
    hidden: fsu.hidden || !fam || !ob || !ob.show,
    act: {
      toggle: () => setFsu((f) => ({ ...f, open: !f.open })),
      hide: () => setFsu((f) => ({ ...f, hidden: true })),
      rules: () => navigate('/help'),
      channel: (c) => guard(() => run(client.request('PUT', '/api/aithora/onboarding/me', { body: { channel: c } }), () => onboarding.reload()))(),
      test: () => guard(() => {
        const c = step('channel')?.channel;
        run(client.request('POST', '/api/aithora/notify/test', { body: { channel: c } }), () => { setFsu((f) => ({ ...f, testSent: true })); onboarding.reload(); });
      })(),
      hello: (kind) => guard(() => {
        setFsu((f) => ({ ...f, hello: kind }));
        if (kind === 'msg' && rid) {
          run(familyMessageSend(client, { params: { residentId: rid }, body: { body: 'Thinking of you, Mom', organizationId: org ?? undefined } }), (r) =>
            run(client.request('PUT', '/api/aithora/onboarding/me', { body: { firstMessageId: r.message.id } }), () => onboarding.reload()));
        }
      })(),
      date: (label, monthDay) => guard(() => {
        if (!rid) return;
        const [mon, day] = monthDay.split(' ');
        const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(mon) + 1;
        run(familyDateAdd(client, { params: { residentId: rid }, body: { label, month, day: Number(day), whose: /^Your/.test(label) ? 'family' : 'resident' } }), (r) => {
          setFsu((f) => ({ ...f, date: label + ', ' + monthDay }));
          run(client.request('PUT', '/api/aithora/onboarding/me', { body: { dateId: r.date.id } }), () => { onboarding.reload(); dates.reload(); });
        });
      })(),
      finish: () => setFsu((f) => ({ ...f, hidden: true })),
    },
  });

  const all: Record<string, unknown> = {
    ...V, ...S, fpFirst: M.resident?.first ?? '',
    // V7: "Today with <resident>" at the top of the portal's home.
    fpTodayCard: <FpTodayCard M={M} act={acts} goTab={(tab) => setUi({ tab })} readOnly={readOnly} readOnlyReason={roReason} real={portal.data ? portal.data.real : null} />,
    // V9 (design fpNoSend): a read-only view (viewing as) gets no reply box, only who is reading.
    fpNoSend: readOnly,
    fpRoWho: 'Read-only as ' + tierLabel(realRole),
    // V9: her emergency (by the claim) and the day note with Ask a question, family only.
    fpV9Top: fam && rid ? (
      <FpV9Top residentId={rid} first={M.resident?.first ?? ''} pronouns={mine.data?.residents[0]?.pronouns ?? null} readOnly={readOnly} readOnlyLine={'Read-only as ' + tierLabel(realRole)} />
    ) : null,
    // V8/V9 home rows: the care team's summary with Message the team, then today, the weekly
    // check-in and how this person hears from the building.
    fpV9Summary: fam && rid ? <FpSummaryRow summary={M.summary ?? ''} onMessage={() => setUi({ tab: 'messages' })} /> : null,
    fpV9Activity: fam && rid ? (
      <FpActivityRow residentId={rid} first={M.resident?.first ?? ''} moments={moments.data} weekly={!!M.weekly} onWeekly={(v) => acts.weekly(v)} readOnly={readOnly} readOnlyLine={'Read-only as ' + tierLabel(realRole)} />
    ) : null,
  };
  const firstError = (aithora ? [coverage] : [mine, inbox, notices, thread, portal]).find((x) => x.error && !x.data)?.error ?? null;
  const noResident = !fam && !aithora && inbox.data && !rid;
  // Until the resident and the portal have answered there is nothing to lay out yet.
  const loading = aithora
    ? coverage.loading && !coverage.data && !coverage.error
    : (fam ? mine.loading && !mine.data && !mine.error : inbox.loading && !inbox.data && !inbox.error) || (on && portal.loading && !portal.data && !portal.error);
  return (
    <div data-screen-label="Family Portal" data-testid="family-portal" style={sx('display:flex;flex-direction:column;gap:9px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      {/* The Family Portal is Family's home: it opens on their greeting (Andrew, 2026-10-19). */}
      {fam && <HomeGreeting />}
      <FpHeadView V={all} />
      {/* Design pwWeekShow: inside a section, never on Home or Today, so Get connected and Today lead. */}
      {fam && on && !loading && rid && ui.tab !== 'home' && ui.tab !== 'today' && <HerWeek residentId={rid} />}
      {loading ? (
        <Skeleton rows={6} label="Loading the Family Portal" />
      ) : aithora ? (
        <div data-testid="family-coverage" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;padding-right:3px;')}>
          <FpBodyView V={{ ...all, fpIsAccess: true }} />
        </div>
      ) : noResident ? (
        <EmptyState title="No family thread is open in your scope" body="A family's view of a resident opens here once a linked family writes in." />
      ) : (
        <>
          <FpUnlinkedView V={all} />
          {all.fpLinked && M.resident && (
            <>
              <FpSetupView V={all} />
              <FpHomeView V={all} />
              <FpPageHeadView V={all} />{' '}
              <div style={sx('flex:' + String(all.fpBodyFlex ?? '1 1 auto') + ';min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:grid;grid-template-columns:repeat(auto-fit,minmax(288px,1fr));gap:11px 16px;align-content:start;align-items:start;padding-right:3px;')}>
                <FpBodyView V={all} />
              </div>{' '}
            </>
          )}
        </>
      )}
      {(err || firstError) && <ErrorState error={(err ?? firstError)!} />}
    </div>
  );
}

/**
 * "Her week" (Console V6, point-of-care.js week; pwLayer): meals, water, sleep and activities
 * for the last 7 days, from what staff charted (GET /family/:residentId/week). A day with
 * nothing recorded has no bar; what Aithora does not record says "Not tracked yet"; when she
 * does not share her health with this person it says why. Not a medical record.
 */
function HerWeek({ residentId }: { residentId: string }) {
  const { client, session } = useSession();
  const q = useQuery((signal) => familyWeek(client, { params: { residentId }, signal }), [client, session?.token, residentId]);
  const w = q.data;
  return (
    <div data-screen-label="Her week" data-testid="her-week" style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px 18px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);')}>
      <div style={sx('grid-column:1/-1;display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:17px;font-weight:800;color:#22272E;')}>Her week</span>
        <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;color:#54606C;')}>{w?.note ?? 'From what staff charted. Not a medical record. Ask the nurse about anything that worries you.'}</span>
      </div>
      {!w && q.loading && <div style={sx('grid-column:1/-1;')}><Skeleton rows={2} label="Loading her week" /></div>}
      {q.error && !w && <div style={sx('grid-column:1/-1;')}><ErrorState error={q.error} onRetry={q.reload} /></div>}
      {w && !w.shared && (
        <div data-testid="her-week-withheld" style={sx('grid-column:1/-1;font-size:16px;color:#3C454F;line-height:1.5;')}>{w.reason}</div>
      )}
      {w && w.shared && w.rows.map((r) => {
        const v = weekBars(r);
        return (
          <div key={r.key} data-testid={'her-week-' + r.key} style={sx('display:flex;flex-direction:column;gap:4px;min-width:0;')}>
            <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
              <span style={sx('flex:1 1 auto;font-size:16px;font-weight:700;color:#22272E;')}>{r.label}</span>
              <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (r.tracked ? '#264C7E' : '#54606C') + ';')}>{v.now}</span>
            </div>
            {r.tracked && (
              <>
                <div aria-hidden="true" style={sx('display:flex;align-items:flex-end;gap:4px;height:30px;')}>
                  {v.bars.map((b, i) => (
                    <span key={i} data-testid="her-week-bar" data-empty={b.pct === null ? 'true' : undefined}
                      style={sx('flex:1 1 0;border-radius:3px 3px 0 0;min-height:3px;height:' + (b.pct ?? 0) + '%;background:' + (b.pct === null ? 'rgba(34,39,46,.10)' : b.today ? '#2F5C97' : 'rgba(47,92,151,.35)') + ';')} />
                  ))}
                </div>
                <div aria-hidden="true" style={sx('display:flex;gap:4px;')}>
                  {w.days.map((d) => <span key={d.date} style={sx('flex:1 1 0;text-align:center;font-size:14px;font-weight:700;color:#54606C;')}>{d.label}</span>)}
                </div>
              </>
            )}
            <span style={sx('font-size:16px;color:#54606C;line-height:1.4;')}>{r.source}</span>
          </div>
        );
      })}
    </div>
  );
}
